import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, Trash2, ArrowLeft, Plus, X, Calendar, TrendingUp, Edit2 } from 'lucide-react';
import { 
  LineChart, 
  Line, 
  Area,
  AreaChart,
  ComposedChart,
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend 
} from 'recharts';
import Layout from '../components/Layout';
import Markdown from '../components/Markdown';
import ConfirmationModal from '../components/ConfirmationModal';
import { 
  getExercise, 
  updateExercise, 
  deleteExercise, 
  logWorkoutStep,
  updateWorkoutLog,
  deleteWorkoutLog
} from '../utils/api';
import { formatDate } from '../utils/date';
import { parseTimeInput } from '../utils/time';

const ExerciseEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showEditLogsModal, setShowEditLogsModal] = useState(false);
  const [config, setConfig] = useState({});
  const [newLogEntry, setNewLogEntry] = useState({});
  const [editingLogId, setEditingLogId] = useState(null);
  const [editingLogData, setEditingLogData] = useState({});
  const [activeChartTab, setActiveChartTab] = useState(null);

  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null);

  useEffect(() => {
    fetchExercise();
  }, [id]);

  const fetchExercise = async () => {
    try {
      const response = await getExercise(id);
      setExercise(response.data);
      setConfig(JSON.parse(response.data.activity_log_config || '{}'));
    } catch (error) {
      console.error('Failed to fetch exercise', error);
      navigate('/exercises');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await updateExercise(id, {
        ...exercise,
        activity_log_config: JSON.stringify(config)
      });
      setEditMode(false);
    } catch (error) {
      console.error('Failed to update exercise', error);
    }
  };

  const handleDelete = async () => {
    setDeleteAction({
      type: 'exercise',
      title: 'Delete Exercise?',
      message: `Are you sure you want to delete "${exercise.title}"? This will also remove all its history. This action cannot be undone.`
    });
    setShowConfirmDelete(true);
  };

  const confirmDeleteExercise = async () => {
    try {
      await deleteExercise(id);
      navigate('/exercises');
    } catch (error) {
      console.error('Failed to delete exercise', error);
    }
  };

  const handleUpdateLog = async (logId) => {
    try {
      const logToUpdate = editingLogData[logId];
      const dataToSave = { ...logToUpdate };
      const dateToSave = dataToSave.date;
      delete dataToSave.date;
      delete dataToSave.id;

      // Normalize keys to lowercase for storage
      const normalizedData = {};
      Object.entries(dataToSave).forEach(([k, v]) => {
        const lowerKey = k.toLowerCase();
        if (lowerKey === 'time') {
          normalizedData[lowerKey] = parseTimeInput(v);
        } else {
          normalizedData[lowerKey] = v;
        }
      });

      await updateWorkoutLog(logId, {
        date: dateToSave,
        data: JSON.stringify(normalizedData)
      });
      setEditingLogId(null);
      fetchExercise();
    } catch (error) {
      console.error('Failed to update log', error);
    }
  };

  const handleLogSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSave = { ...newLogEntry };
      const dateToSave = dataToSave.date || new Date().toISOString().split('T')[0];
      delete dataToSave.date;

      // Normalize keys to lowercase for storage
      const normalizedData = {};
      Object.entries(dataToSave).forEach(([k, v]) => {
        const lowerKey = k.toLowerCase();
        if (lowerKey === 'time') {
          normalizedData[lowerKey] = parseTimeInput(v);
        } else {
          normalizedData[lowerKey] = v;
        }
      });

      await logWorkoutStep({
        exercise_id: id,
        date: dateToSave,
        data: normalizedData
      });

      setShowLogModal(false);
      setNewLogEntry({});
      fetchExercise();
    } catch (error) {
      console.error('Failed to add log entry', error);
    }
  };

  const toggleConfig = async (field) => {
    const newConfig = { ...config, [field]: !config[field] };
    setConfig(newConfig);
    
    // Auto-save the config change
    try {
      const configJson = JSON.stringify(newConfig);
      await updateExercise(id, {
        ...exercise,
        activity_log_config: configJson
      });
      // Also update the local exercise state to keep it in sync
      setExercise(prev => ({ ...prev, activity_log_config: configJson }));
    } catch (error) {
      console.error('Failed to update exercise configuration', error);
    }
  };


  const handleDeleteLog = async (logId) => {
    setDeleteAction({
      type: 'log',
      id: logId,
      title: 'Delete Log Entry?',
      message: 'Are you sure you want to delete this specific log entry? This action cannot be undone.'
    });
    setShowConfirmDelete(true);
  };

  const confirmDeleteLog = async (logId) => {
    try {
      await deleteWorkoutLog(logId);
      fetchExercise();
    } catch (error) {
      console.error('Failed to delete log', error);
    }
  };

  const handleConfirmDelete = () => {
    if (deleteAction.type === 'exercise') {
      confirmDeleteExercise();
    } else if (deleteAction.type === 'log') {
      confirmDeleteLog(deleteAction.id);
    }
    setShowConfirmDelete(false);
    setDeleteAction(null);
  };

  const startEditingLog = (log) => {
    setEditingLogId(log.id);
    setEditingLogData({
      ...editingLogData,
      [log.id]: {
        ...log.data,
        date: log.date,
        id: log.id
      }
    });
  };

  const activeFields = useMemo(() => {
    const fields = Object.entries(config)
      .filter(([_, active]) => active)
      .map(([field]) => field.toLowerCase());
    return Array.from(new Set(fields));
  }, [config]);

  const chartData = useMemo(() => {
    if (!exercise || !exercise.logs || exercise.logs.length === 0) return [];
    
    // Sort logs by date AND then by ID
    const sortedLogs = [...exercise.logs].sort((a, b) => {
      const dateDiff = new Date(a.date) - new Date(b.date);
      if (dateDiff !== 0) return dateDiff;
      return a.id - b.id;
    });

    // Group logs by global_session_id
    const sessions = {};
    sortedLogs.forEach(log => {
      const sessionId = log.global_session_id || `manual-${log.date}-${log.id}`;
      if (!sessions[sessionId]) {
        sessions[sessionId] = {
          id: sessionId,
          date: log.date,
          logs: []
        };
      }
      sessions[sessionId].logs.push(log);
    });

    // Convert sessions to chart data points
    const sessionArray = Object.values(sessions).sort((a, b) => {
      const dateDiff = new Date(a.date) - new Date(b.date);
      if (dateDiff !== 0) return dateDiff;
      // If same date, sort by the ID of the first log in each session
      return a.logs[0].id - b.logs[0].id;
    });

    // Count sessions per date to handle "(R)" suffix
    const dateCounts = {};
    sessionArray.forEach(s => {
      dateCounts[s.date] = (dateCounts[s.date] || 0) + 1;
    });

    const dateSeen = {};

    return sessionArray.map((session) => {
      const dateObj = new Date(session.date);
      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      let dateLabel = `${day}/${month}`;
      
      if (dateCounts[session.date] > 1) {
        dateSeen[session.date] = (dateSeen[session.date] || 0) + 1;
        dateLabel += ' (R)';
      }

      const entry = { 
        date: dateLabel,
        fullDate: session.date,
        id: session.id,
      };
      
      // Calculate min/max for each active field
      activeFields.forEach(field => {
        const values = session.logs
          .map(log => {
            const val = log.data ? log.data[field] : null;
            return typeof val === 'string' ? parseFloat(val) : val;
          })
          .filter(v => v !== null && !isNaN(v));

        if (values.length > 0) {
          entry[`${field}_max`] = Math.max(...values);
          entry[`${field}_min`] = Math.min(...values);
          // For backward compatibility or single line if min === max
          entry[field] = entry[`${field}_max`]; 
        }
      });

      return entry;
    });
  }, [exercise, activeFields]);

  useEffect(() => {
    if (activeFields.length > 0 && !activeChartTab) {
      setActiveChartTab(activeFields[0]);
    } else if (activeFields.length === 0) {
      setActiveChartTab(null);
    } else if (activeFields.length > 0 && !activeFields.includes(activeChartTab)) {
      setActiveChartTab(activeFields[0]);
    }
  }, [activeFields, activeChartTab]);

  if (loading) return <Layout>Loading...</Layout>;

  return (
    <Layout title={exercise.title} subtitle={exercise.subtitle || "Exercise Details"}>
      <div className="md:hidden mb-6">
        <h2 className="text-2xl font-black uppercase italic tracking-tighter leading-tight">{exercise.title}</h2>
        {exercise.subtitle && <p className="text-xs text-[#A3E635] font-bold uppercase tracking-wider">{exercise.subtitle}</p>}
      </div>

      <div className="flex justify-between mb-8">
        <button 
          onClick={() => navigate('/exercises')} 
          className="bg-[#A3E635] text-black px-6 py-2 rounded-full font-black text-sm flex items-center gap-2 hover:scale-105 transition-transform"
        >
          {"<< BACK"}
        </button>
        <div className="flex gap-4">
          <button 
            onClick={() => setEditMode(!editMode)}
            className="bg-[#A3E635] text-black px-6 py-2 rounded-full font-black text-sm hover:scale-105 transition-transform"
          >
            {editMode ? 'CANCEL' : 'EDIT'}
          </button>
          <button 
            onClick={handleDelete}
            className="p-2 rounded-full border border-red-900/50 text-red-500 hover:bg-red-500 hover:text-white transition-all"
          >
            <Trash2 size={20} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Technical Sheet */}
        <div className="bg-[#1C1C1E] rounded-[32px] p-8 border border-[#2C2C2E]">
          <h2 className="text-xl font-black italic tracking-tighter mb-6 uppercase">Technical Sheet</h2>
          
          {editMode ? (
            <div className="space-y-6">
              <div>
                <label className="block text-[10px] text-slate-500 font-bold uppercase mb-2">Title</label>
                <input
                  type="text"
                  value={exercise.title}
                  onChange={(e) => setExercise({ ...exercise, title: e.target.value })}
                  className="w-full bg-[#2C2C2E] border border-transparent focus:border-[#A3E635] outline-none rounded-2xl px-6 py-4 transition-all"
                />
              </div>
              <div>
                <label className="block text-[10px] text-slate-500 font-bold uppercase mb-2">Subtitle</label>
                <input
                  type="text"
                  placeholder="Focus on the contraction..."
                  value={exercise.subtitle || ''}
                  onChange={(e) => setExercise({ ...exercise, subtitle: e.target.value })}
                  className="w-full bg-[#2C2C2E] border border-transparent focus:border-[#A3E635] outline-none rounded-2xl px-6 py-4 transition-all"
                />
              </div>
              <div>
                <label className="block text-[10px] text-slate-500 font-bold uppercase mb-2">Description (Markdown)</label>
                <textarea
                  rows={10}
                  value={exercise.description}
                  onChange={(e) => setExercise({ ...exercise, description: e.target.value })}
                  className="w-full bg-[#2C2C2E] border border-transparent focus:border-[#A3E635] outline-none rounded-2xl px-6 py-4 transition-all font-mono text-sm"
                />
              </div>
              <button 
                onClick={handleSave}
                className="w-full bg-[#A3E635] text-black font-black py-4 rounded-full flex items-center justify-center gap-2"
              >
                <Save size={20} /> SAVE CHANGES
              </button>
            </div>
          ) : (
            <Markdown content={exercise.description || '_No description provided._'} />
          )}
        </div>

        {/* Activity Log Config */}
        <div className="space-y-8">
          <div className="bg-[#1C1C1E] rounded-[32px] p-8 border border-[#2C2C2E]">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-black italic tracking-tighter uppercase">Activity Log Columns</h2>
              <button 
                onClick={() => setShowLogModal(true)}
                className="p-2 rounded-full bg-[#A3E635] text-black hover:scale-110 transition-transform"
                title="Add log entry"
              >
                <Plus size={20} />
              </button>
            </div>
            <p className="text-slate-500 text-sm mb-6">Select which data points to track for this exercise.</p>
            
            <div className="flex flex-wrap gap-3">
              {['weight', 'reps', 'time', 'distance', 'bpm'].map(field => (
                <button
                  key={field}
                  onClick={() => toggleConfig(field)}
                  className={`px-6 py-3 rounded-full font-bold uppercase text-xs transition-all border ${
                    config[field] 
                      ? 'bg-[#A3E635] text-black border-[#A3E635]' 
                      : 'bg-transparent text-slate-500 border-[#2C2C2E] hover:border-slate-600'
                  }`}
                >
                  {field}
                </button>
              ))}
            </div>
            
            {editMode && (
              <div className="mt-8 pt-8 border-t border-[#2C2C2E]">
                <p className="text-xs text-[#A3E635] font-bold uppercase mb-4 opacity-70 italic">
                  Changes to columns are saved automatically
                </p>
                <button 
                  onClick={handleSave}
                  className="text-[#A3E635] font-bold text-sm flex items-center gap-2 hover:underline"
                >
                  Update technical sheet
                </button>
              </div>
            )}
          </div>

          <div className={`bg-[#1C1C1E] rounded-[32px] p-8 border border-[#2C2C2E] ${chartData.length === 0 ? 'opacity-50' : ''}`}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-black italic tracking-tighter uppercase">Performance Chart</h2>
              <div className="flex gap-2">
                {chartData.length > 0 && (
                  <button 
                    onClick={() => setShowEditLogsModal(true)}
                    className="p-2 rounded-full bg-[#A3E635] text-black hover:scale-110 transition-transform"
                    title="Edit log entries"
                  >
                    <Edit2 size={20} />
                  </button>
                )}
              </div>
            </div>

            {activeFields.length > 1 && chartData.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-8 bg-[#2C2C2E]/30 p-1 rounded-[20px] w-fit">
                {activeFields.map((field) => (
                  <button
                    key={field}
                    onClick={() => setActiveChartTab(field)}
                    className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-wider transition-all ${
                      activeChartTab === field 
                        ? 'bg-[#A3E635] text-black shadow-[0_0_15px_rgba(163,230,53,0.3)]' 
                        : 'text-slate-500 hover:text-white'
                    }`}
                  >
                    {field}
                  </button>
                ))}
              </div>
            )}
            
            <div className="h-64 flex items-center justify-center">
              {chartData.length > 0 && activeChartTab ? (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData}>
                    <defs>
                      <linearGradient id="colorAreaActive" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#A3E635" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#A3E635" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2C2C2E" vertical={false} />
                    <XAxis 
                      dataKey="date" 
                      stroke="#475569" 
                      fontSize={10} 
                      tickLine={false} 
                      axisLine={false} 
                    />
                    <YAxis 
                      stroke="#475569" 
                      fontSize={10} 
                      tickLine={false} 
                      axisLine={false}
                      tickFormatter={(value) => `${value}`}
                    />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1C1C1E', border: '1px solid #2C2C2E', borderRadius: '16px' }}
                      itemStyle={{ fontSize: '12px', fontWeight: 'bold' }}
                      labelFormatter={(date) => {
                        const item = chartData.find(d => d.date === date);
                        return item ? item.fullDate : date;
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey={activeChartTab}
                      stroke="none"
                      fill="url(#colorAreaActive)"
                      legendType="none"
                      tooltipType="none"
                      connectNulls
                      name=""
                      isAnimationActive={false}
                    />
                    {chartData.some(d => d[`${activeChartTab}_max`] !== d[`${activeChartTab}_min`]) && (
                      <Line
                        type="monotone"
                        dataKey={`${activeChartTab}_min`}
                        stroke="#38BDF8"
                        strokeWidth={3}
                        dot={{ fill: '#1C1C1E', strokeWidth: 2, r: 4 }}
                        activeDot={{ r: 6, strokeWidth: 0 }}
                        name={`${activeChartTab.charAt(0).toUpperCase() + activeChartTab.slice(1)} (Min)`}
                        connectNulls
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey={`${activeChartTab}_max`}
                      stroke="#A3E635"
                      strokeWidth={3}
                      dot={{ fill: '#1C1C1E', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, strokeWidth: 0 }}
                      name={chartData.some(d => d[`${activeChartTab}_max`] !== d[`${activeChartTab}_min`]) ? `${activeChartTab.charAt(0).toUpperCase() + activeChartTab.slice(1)} (Max)` : activeChartTab.charAt(0).toUpperCase() + activeChartTab.slice(1)}
                      connectNulls
                    />
                    {chartData.some(d => d[`${activeChartTab}_max`] !== d[`${activeChartTab}_min`]) && (
                      <Legend 
                        verticalAlign="top" 
                        align="right" 
                        iconType="circle"
                        wrapperStyle={{ paddingTop: '0px', paddingBottom: '20px', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase' }}
                        payload={[
                          { value: `${activeChartTab.charAt(0).toUpperCase() + activeChartTab.slice(1)} (Max)`, type: 'circle', id: `${activeChartTab}_max`, color: '#A3E635' },
                          { value: `${activeChartTab.charAt(0).toUpperCase() + activeChartTab.slice(1)} (Min)`, type: 'circle', id: `${activeChartTab}_min`, color: '#38BDF8' }
                        ]}
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              ) : chartData.length > 0 ? (
                <div className="text-slate-600 font-bold uppercase text-[10px]">Select a field to view chart</div>
              ) : (
                <div className="flex flex-col items-center gap-4 text-center">
                  <div className="p-4 rounded-full bg-[#2C2C2E] text-slate-500">
                    <TrendingUp size={32} />
                  </div>
                  <p className="text-slate-600 font-bold uppercase text-[10px] max-w-[160px]">
                    Data will appear after your first recorded logs
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Log Entry Modal */}
      {showLogModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowLogModal(false)} />
          <div className="relative bg-[#1C1C1E] border border-[#2C2C2E] rounded-[32px] w-full max-w-md p-8 animate-in zoom-in duration-200">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-black italic tracking-tighter uppercase">Add Log Entry</h2>
              <button onClick={() => setShowLogModal(false)} className="text-slate-500 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleLogSubmit} className="space-y-6">
              <div>
                <label className="block text-[10px] text-slate-500 font-bold uppercase mb-2">Date</label>
                <div className="relative">
                  <input
                    type="date"
                    required
                    value={newLogEntry.date || new Date().toISOString().split('T')[0]}
                    onChange={(e) => setNewLogEntry({ ...newLogEntry, date: e.target.value })}
                    className="w-full bg-[#2C2C2E] border border-transparent focus:border-[#A3E635] outline-none rounded-2xl px-6 py-4 transition-all"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                {activeFields.map((field) => {
                  const isTimeField = field.toLowerCase() === 'time';
                  return (
                    <div key={field}>
                      <label className="block text-[10px] text-slate-500 font-bold uppercase mb-2">{field}</label>
                      <input
                        type={isTimeField ? "text" : "number"}
                        step="any"
                        placeholder={isTimeField ? "00:00" : "0.0"}
                        value={newLogEntry[field] || ''}
                        onChange={(e) => setNewLogEntry({ ...newLogEntry, [field]: e.target.value })}
                        className="w-full bg-[#2C2C2E] border border-transparent focus:border-[#A3E635] outline-none rounded-2xl px-6 py-4 transition-all"
                      />
                    </div>
                  );
                })}
              </div>
              
              <div>
                <label className="block text-[10px] text-slate-500 font-bold uppercase mb-2">Notes (Markdown)</label>
                <textarea
                  placeholder="Notes about this log..."
                  value={newLogEntry.notes || ''}
                  onChange={(e) => setNewLogEntry({ ...newLogEntry, notes: e.target.value })}
                  className="w-full bg-[#2C2C2E] border border-transparent focus:border-[#A3E635] outline-none rounded-2xl px-6 py-4 transition-all h-24"
                />
              </div>
              
              <button 
                type="submit"
                className="w-full bg-[#A3E635] text-black font-black py-4 rounded-full flex items-center justify-center gap-2 mt-4"
              >
                <Save size={20} /> SAVE ENTRY
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Edit Logs Modal */}
      {showEditLogsModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowEditLogsModal(false)} />
          <div className="relative bg-[#1C1C1E] border border-[#2C2C2E] rounded-[32px] w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col p-8 animate-in zoom-in duration-200">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-black italic tracking-tighter uppercase">Edit Log Entries</h2>
              <button onClick={() => setShowEditLogsModal(false)} className="text-slate-500 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="overflow-x-auto flex-1">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-[#2C2C2E]">
                    <th className="pb-4 px-4 text-[10px] text-slate-500 font-black uppercase">Date</th>
                    {activeFields.map(field => (
                      <th key={field} className="pb-4 px-4 text-[10px] text-slate-500 font-black uppercase">{field}</th>
                    ))}
                    <th className="pb-4 px-4 text-[10px] text-slate-500 font-black uppercase">Notes</th>
                    <th className="pb-4 px-4 text-[10px] text-slate-500 font-black uppercase text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2C2C2E]">
                  {[...(exercise.logs || [])].reverse().map(log => (
                    <tr key={log.id} className="hover:bg-white/5 transition-colors">
                      <td className="py-4 px-4">
                        {editingLogId === log.id ? (
                          <input
                            type="date"
                            value={editingLogData[log.id]?.date || ''}
                            onChange={(e) => setEditingLogData({
                              ...editingLogData,
                              [log.id]: { ...editingLogData[log.id], date: e.target.value }
                            })}
                            className="bg-[#2C2C2E] border-none rounded-lg px-2 py-1 text-sm focus:ring-1 ring-[#A3E635] outline-none text-white"
                          />
                        ) : (
                          <span className="text-sm font-medium">{log.date}</span>
                        )}
                      </td>
                      {activeFields.map(field => {
                        const isTimeField = field.toLowerCase() === 'time';
                        return (
                          <td key={field} className="py-4 px-4">
                            {editingLogId === log.id ? (
                              <input
                                type={isTimeField ? "text" : "number"}
                                step="any"
                                value={editingLogData[log.id]?.[field] || ''}
                                onChange={(e) => setEditingLogData({
                                  ...editingLogData,
                                  [log.id]: { ...editingLogData[log.id], [field]: e.target.value }
                                })}
                                className="w-20 bg-[#2C2C2E] border-none rounded-lg px-2 py-1 text-sm focus:ring-1 ring-[#A3E635] outline-none text-white"
                              />
                            ) : (
                              <span className="text-sm font-bold text-slate-300">
                                {/* Search for value in log.data using case-insensitive key matching */}
                                {(() => {
                                  const entry = Object.entries(log.data || {}).find(([k]) => k.toLowerCase() === field);
                                  return entry ? entry[1] : '--';
                                })()}
                              </span>
                            )}
                          </td>
                        );
                      })}
                      <td className="py-4 px-4">
                        {editingLogId === log.id ? (
                          <textarea
                            value={editingLogData[log.id]?.notes || ''}
                            onChange={(e) => setEditingLogData({
                              ...editingLogData,
                              [log.id]: { ...editingLogData[log.id], notes: e.target.value }
                            })}
                            className="w-48 bg-[#2C2C2E] border-none rounded-lg px-2 py-1 text-sm focus:ring-1 ring-[#A3E635] outline-none text-white h-16"
                          />
                        ) : (
                          <div className="max-w-[200px] text-xs">
                             <Markdown content={log.data?.notes || ''} />
                          </div>
                        )}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <div className="flex justify-end gap-2">
                          {editingLogId === log.id ? (
                            <>
                              <button 
                                onClick={() => handleUpdateLog(log.id)}
                                className="p-2 rounded-full bg-[#A3E635] text-black hover:scale-110 transition-transform"
                              >
                                <Save size={16} />
                              </button>
                              <button 
                                onClick={() => setEditingLogId(null)}
                                className="p-2 rounded-full bg-[#2C2C2E] text-white hover:bg-slate-700 transition-colors"
                              >
                                <X size={16} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button 
                                onClick={() => startEditingLog(log)}
                                className="p-2 rounded-full border border-[#2C2C2E] text-slate-400 hover:text-white hover:border-white transition-all"
                              >
                                <Edit2 size={16} />
                              </button>
                              <button 
                                onClick={() => handleDeleteLog(log.id)}
                                className="p-2 rounded-full border border-red-900/50 text-red-500 hover:bg-red-500 hover:text-white transition-all"
                              >
                                <Trash2 size={16} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!exercise.logs || exercise.logs.length === 0) && (
                <div className="py-12 text-center text-slate-500 font-bold uppercase text-[10px]">
                  No log entries found
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={handleConfirmDelete}
        title={deleteAction?.title}
        message={deleteAction?.message}
      />
    </Layout>
  );
};

export default ExerciseEditor;
